export default class RetryHandler {
  constructor(options = {}) {
    this.maxRetries = options.maxRetries || 3;
    this.baseDelay = options.baseDelay || 1000;
    this.maxDelay = options.maxDelay || 30000;
    this.backoffMultiplier = options.backoffMultiplier || 2;
    this.retryableStatusCodes = options.retryableStatusCodes || [
      408, // Request Timeout
      429, // Too Many Requests
      500, // Internal Server Error
      502, // Bad Gateway
      503, // Service Unavailable
      504, // Gateway Timeout
      522, // Connection Timed Out
      524, // A Timeout Occurred
    ];
    this.retryableNetworkErrors = options.retryableNetworkErrors !== false;
  }

  shouldRetry(error, attempt) {
    if (attempt >= this.maxRetries) {
      return false;
    }

    // Network errors (fetch failures)
    if (this.retryableNetworkErrors && this.isNetworkError(error)) {
      return true;
    }

    // HTTP status code errors
    if (error.status && this.retryableStatusCodes.includes(error.status)) {
      return true;
    }

    return false;
  }

  isNetworkError(error) {
    if (!error) return false;

    return Boolean(
      error.name === 'TypeError' ||
      error.name === 'NetworkError' ||
      (error.message && error.message.includes('fetch')) ||
      (error.message && error.message.includes('network')) ||
      (error.message && error.message.includes('ECONNRESET')) ||
      (error.message && error.message.includes('ENOTFOUND')) ||
      (error.message && error.message.includes('ETIMEDOUT'))
    );
  }

  calculateDelay(attempt) {
    const exponentialDelay =
      this.baseDelay * Math.pow(this.backoffMultiplier, attempt);
    const jitter = Math.random() * 0.3 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, this.maxDelay);
  }

  async sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async executeWithRetry(requestFunction, options = {}) {
    const retryOptions = { ...this, ...options };
    let lastError;

    for (let attempt = 0; attempt <= retryOptions.maxRetries; attempt++) {
      try {
        const result = await requestFunction();
        return result;
      } catch (error) {
        lastError = error;

        if (!this.shouldRetry(error, attempt)) {
          throw error;
        }

        if (attempt < retryOptions.maxRetries) {
          const delay = this.calculateDelay(attempt);
          await this.sleep(delay);
        }
      }
    }

    throw lastError;
  }

  static create(options = {}) {
    return new RetryHandler(options);
  }
}
